import React, { useEffect, useState } from 'react'
import {
  getEntries,
  subscribe,
  clear,
  isPaused,
  togglePaused,
  buildCurl,
} from '../../api/apiHistory'
import styles from './ApiDevPanel.module.css'

const OPEN_KEY = 'ngrs_api_panel_open'

/**
 * Bottom-right floating "API" button → opens a side drawer showing the last
 * 100 API calls captured by the axios interceptor. Each entry expands to show
 * request/response headers + body + a copy-able cURL.
 *
 * Educational tool — lets demo viewers see the UIC-shaped JSON the SPA
 * exchanges with the backend without opening DevTools.
 */
const ApiDevPanel = () => {
  const [entries, setEntries] = useState(getEntries())
  const [open, setOpen] = useState(() => localStorage.getItem(OPEN_KEY) === '1')
  const [expandedId, setExpandedId] = useState(null)
  const [tab, setTab] = useState('response')
  const [paused, setPaused] = useState(isPaused())
  const [revealToken, setRevealToken] = useState(false)
  const [copyToast, setCopyToast] = useState('')

  useEffect(() => subscribe((e) => setEntries([...e])), [])
  useEffect(() => {
    localStorage.setItem(OPEN_KEY, open ? '1' : '0')
  }, [open])
  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open])

  const expandedEntry = entries.find((e) => e.id === expandedId) || null

  const handleCopy = async (text, label = 'Copied') => {
    try {
      await navigator.clipboard.writeText(text)
      setCopyToast(label)
      setTimeout(() => setCopyToast(''), 1500)
    } catch {
      setCopyToast('Copy failed')
      setTimeout(() => setCopyToast(''), 1500)
    }
  }

  const statusClass = (e) => {
    if (e.status === 0) return styles.statusErr
    if (e.status >= 200 && e.status < 300) return styles.statusOk
    if (e.status >= 400 && e.status < 500) return styles.statusWarn
    return styles.statusErr
  }

  return (
    <>
      <button
        className={styles.fab + (open ? ' ' + styles.fabOpen : '')}
        onClick={() => setOpen((v) => !v)}
        title="API debug panel — request / response viewer"
        aria-label="Toggle API debug panel"
      >
        <span className={styles.fabIcon}>{'{}'}</span>
        <span className={styles.fabLabel}>API</span>
        {entries.length > 0 && (
          <span className={styles.fabBadge}>{entries.length}</span>
        )}
      </button>

      {open && (
        <div className={styles.drawer} role="dialog" aria-modal="true" aria-label="API request history">
          <header className={styles.drawerHead}>
            <div className={styles.drawerTitle}>
              <span className={styles.drawerTitleMain}>API calls</span>
              <span className={styles.drawerTitleSub}>
                last {entries.length} · {paused ? 'paused' : 'live'}
              </span>
            </div>
            <div className={styles.drawerActions}>
              <button
                className={styles.btn}
                onClick={() => {
                  const next = togglePaused()
                  setPaused(next)
                }}
                title={paused ? 'Resume capture' : 'Pause capture'}
              >
                {paused ? '▶' : '❚❚'}
              </button>
              <button
                className={styles.btn}
                onClick={() => {
                  clear()
                  setExpandedId(null)
                }}
                title="Clear all"
              >
                Clear
              </button>
              <button
                className={styles.btn}
                onClick={() => setOpen(false)}
                aria-label="Close"
                title="Close"
              >
                ×
              </button>
            </div>
          </header>

          <div className={styles.drawerBody}>
            <ul className={styles.list}>
              {entries.length === 0 ? (
                <li className={styles.empty}>
                  No API calls captured yet. Navigate around to populate.
                </li>
              ) : (
                entries.map((e) => {
                  const isOpen = expandedId === e.id
                  return (
                    <li
                      key={e.id}
                      className={styles.item + (isOpen ? ' ' + styles.itemOpen : '')}
                    >
                      <button
                        className={styles.itemHead}
                        onClick={() => {
                          setExpandedId(isOpen ? null : e.id)
                          setTab('response')
                        }}
                      >
                        <span className={styles.method + ' ' + styles['method' + e.method]}>
                          {e.method}
                        </span>
                        <span className={styles.url}>{e.url}</span>
                        <span className={styles.status + ' ' + statusClass(e)}>
                          {e.status || '—'}
                        </span>
                        <span className={styles.duration}>{e.duration}ms</span>
                      </button>

                      {isOpen && expandedEntry && (
                        <div className={styles.detail}>
                          <div className={styles.tabs}>
                            {['request', 'response', 'curl'].map((t) => (
                              <button
                                key={t}
                                className={styles.tab + (tab === t ? ' ' + styles.tabActive : '')}
                                onClick={() => setTab(t)}
                              >
                                {t.charAt(0).toUpperCase() + t.slice(1)}
                              </button>
                            ))}
                          </div>

                          {tab === 'request' && (
                            <div className={styles.pane}>
                              <Section title="Headers">
                                <Json value={expandedEntry.requestHeaders} />
                              </Section>
                              <Section title="Body">
                                {expandedEntry.requestBody == null ? (
                                  <p className={styles.muted}>No body</p>
                                ) : (
                                  <Json value={expandedEntry.requestBody} />
                                )}
                              </Section>
                            </div>
                          )}

                          {tab === 'response' && (
                            <div className={styles.pane}>
                              <Section title={`Status ${expandedEntry.status}`}>
                                <p className={styles.muted}>
                                  {expandedEntry.duration}ms ·{' '}
                                  {new Date(expandedEntry.timestamp).toLocaleTimeString()}
                                </p>
                              </Section>
                              <Section title="Headers">
                                <Json value={expandedEntry.responseHeaders} />
                              </Section>
                              <Section title="Body">
                                <Json value={expandedEntry.responseBody} />
                              </Section>
                              <div className={styles.paneActions}>
                                <button
                                  className={styles.btn}
                                  onClick={() =>
                                    handleCopy(
                                      JSON.stringify(expandedEntry.responseBody, null, 2),
                                      'Response copied'
                                    )
                                  }
                                >
                                  Copy response JSON
                                </button>
                              </div>
                            </div>
                          )}

                          {tab === 'curl' && (
                            <div className={styles.pane}>
                              <label className={styles.checkRow}>
                                <input
                                  type="checkbox"
                                  checked={revealToken}
                                  onChange={(e) => setRevealToken(e.target.checked)}
                                />
                                <span>Include real Authorization token (sensitive)</span>
                              </label>
                              <pre className={styles.curl}>
                                {buildCurl(expandedEntry, { reveal: revealToken })}
                              </pre>
                              <div className={styles.paneActions}>
                                <button
                                  className={styles.btn}
                                  onClick={() =>
                                    handleCopy(
                                      buildCurl(expandedEntry, { reveal: revealToken }),
                                      'cURL copied'
                                    )
                                  }
                                >
                                  Copy cURL
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  )
                })
              )}
            </ul>
          </div>

          {copyToast && <div className={styles.toast}>{copyToast}</div>}
        </div>
      )}
    </>
  )
}

const Section = ({ title, children }) => (
  <div className={styles.section}>
    <div className={styles.sectionTitle}>{title}</div>
    {children}
  </div>
)

const Json = ({ value }) => {
  let str
  try {
    str = typeof value === 'string' ? value : JSON.stringify(value, null, 2)
  } catch {
    str = String(value)
  }
  return <pre className={styles.json}>{str}</pre>
}

export default ApiDevPanel
